import { supabaseClient } from './supabase';

/** Row from public.california_cap_ghg_targets (loaded by python/california_cap_ghg_targets.py). */
export type CaliforniaCapGhgTargetRow = {
  geofips: number;
  location_name: string | null;
  county: string | null;
  geography_type: string | null;
  adoption_year: string | null;
  percent_below: number | string | null;
  baseline_year: number | null;
  target_year: number | null;
  /**
   * Plan-stated baseline year when it was before 2005 (e.g. 2000 in “below 2000 by 2050”).
   * `baseline_year` in this row is then 2005 for inventory alignment; Policy Tool banner + reference-year tooltip explain that we use 2005 instead of this value.
   */
  official_baseline_year: number | null;
  /** 'carbon_neutral' | 'ghg_target_1' | 'ghg_target_2' | 'ghg_target_3' | null — from CAP ingest. */
  target_source: string | null;
  source_label?: string | null;
};

/**
 * Fetch parsed CAP GHG target row for a place (geofips matches population / livability).
 */
export async function fetchCaliforniaCapGhgTargetForGeofips(
  geofips: number
): Promise<CaliforniaCapGhgTargetRow | null> {
  if (!Number.isFinite(geofips)) return null;

  const { data, error } = await supabaseClient
    .from('california_cap_ghg_targets')
    .select(
      'geofips,location_name,county,geography_type,adoption_year,percent_below,baseline_year,target_year,official_baseline_year,target_source,source_label'
    )
    .eq('geofips', geofips)
    .maybeSingle();

  if (error) {
    return null;
  }
  return data as CaliforniaCapGhgTargetRow | null;
}

/** Four-digit adoption year for UI copy, or null if not parseable. */
export function parseCapAdoptionDisplayYear(adoptionYear: string | null | undefined): string | null {
  if (adoptionYear == null) return null;
  const s = String(adoptionYear).trim();
  if (/^\d{4}$/.test(s)) return s;
  const n = parseInt(s.replace(/,/g, ''), 10);
  if (Number.isFinite(n) && n >= 1900 && n <= 2100) return String(n);
  return null;
}
